import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ChevronDownIcon, ChevronUpIcon, MagnifyingGlassIcon, XMarkIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setOpenItems(new Set());
  };

  const handleBack = () => {
    // Check if there's a previous page in history
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // If no history, go to home page
      navigate('/');
    }
  };

  const faqData: FAQItem[] = [
    {
      question: t('faq.general.whatIsGnzabe.question'),
      answer: t('faq.general.whatIsGnzabe.answer')
    },
    {
      question: t('faq.general.whatMakesDifferent.question'),
      answer: t('faq.general.whatMakesDifferent.answer')
    },
    {
      question: t('faq.general.whoCanUse.question'),
      answer: t('faq.general.whoCanUse.answer')
    },
    {
      question: t('faq.gettingStarted.howToCreateAccount.question'),
      answer: t('faq.gettingStarted.howToCreateAccount.answer')
    },
    {
      question: t('faq.gettingStarted.companyVsIndividual.question'),
      answer: t('faq.gettingStarted.companyVsIndividual.answer')
    },
    {
      question: t('faq.gettingStarted.approvalTime.question'),
      answer: t('faq.gettingStarted.approvalTime.answer')
    },
    {
      question: t('faq.gettingStarted.forgotPassword.question'),
      answer: t('faq.gettingStarted.forgotPassword.answer')
    },
    {
      question: t('faq.roles.employeePermissions.question'),
      answer: t('faq.roles.employeePermissions.answer')
    },
    {
      question: t('faq.roles.departmentAdminPermissions.question'),
      answer: t('faq.roles.departmentAdminPermissions.answer')
    },
    {
      question: t('faq.roles.companyAdminPermissions.question'),
      answer: t('faq.roles.companyAdminPermissions.answer')
    },
    {
      question: t('faq.courses.howToAccess.question'),
      answer: t('faq.courses.howToAccess.answer')
    },
    {
      question: t('faq.courses.typesAvailable.question'),
      answer: t('faq.courses.typesAvailable.answer')
    },
    {
      question: t('faq.courses.courseStructure.question'),
      answer: t('faq.courses.courseStructure.answer')
    },
    {
      question: t('faq.courses.retakeQuizzes.question'),
      answer: t('faq.courses.retakeQuizzes.answer')
    },
    {
      question: t('faq.courses.completionDeadline.question'),
      answer: t('faq.courses.completionDeadline.answer')
    },
    {
      question: t('faq.courses.certificates.question'),
      answer: t('faq.courses.certificates.answer')
    },
    {
      question: t('faq.analytics.whatAnalytics.question'),
      answer: t('faq.analytics.whatAnalytics.answer')
    },
    {
      question: t('faq.analytics.updateFrequency.question'),
      answer: t('faq.analytics.updateFrequency.answer')
    },
    {
      question: t('faq.analytics.exportData.question'),
      answer: t('faq.analytics.exportData.answer')
    },
    {
      question: t('faq.technical.supportedBrowsers.question'),
      answer: t('faq.technical.supportedBrowsers.answer')
    },
    {
      question: t('faq.technical.mobileApp.question'),
      answer: t('faq.technical.mobileApp.answer')
    },
    {
      question: t('faq.technical.technicalIssues.question'),
      answer: t('faq.technical.technicalIssues.answer')
    },
    {
      question: t('faq.technical.enableNotifications.question'),
      answer: t('faq.technical.enableNotifications.answer')
    },
    {
      question: t('faq.security.dataSecure.question'),
      answer: t('faq.security.dataSecure.answer')
    },
    {
      question: t('faq.security.dataCollected.question'),
      answer: t('faq.security.dataCollected.answer')
    },
    {
      question: t('faq.security.deleteAccount.question'),
      answer: t('faq.security.deleteAccount.answer')
    },
    {
      question: t('faq.security.learningProgress.question'),
      answer: t('faq.security.learningProgress.answer')
    },
    {
      question: t('faq.troubleshooting.cantLogin.question'),
      answer: t('faq.troubleshooting.cantLogin.answer')
    },
    {
      question: t('faq.troubleshooting.courseWontLoad.question'),
      answer: t('faq.troubleshooting.courseWontLoad.answer')
    },
    {
      question: t('faq.troubleshooting.courseNotCompleted.question'),
      answer: t('faq.troubleshooting.courseNotCompleted.answer')
    },
    {
      question: t('faq.troubleshooting.cantSeeCourses.question'),
      answer: t('faq.troubleshooting.cantSeeCourses.answer')
    },
    {
      question: t('faq.troubleshooting.leaderboardNotUpdating.question'),
      answer: t('faq.troubleshooting.leaderboardNotUpdating.answer')
    },
    {
      question: t('faq.troubleshooting.notificationIssues.question'),
      answer: t('faq.troubleshooting.notificationIssues.answer')
    }
  ];

  // Search functionality
  const filteredFAQData = useMemo(() => {
    if (!searchQuery.trim()) return faqData;
    
    const query = searchQuery.toLowerCase().trim();
    return faqData.filter(item => 
      item.question.toLowerCase().includes(query) || 
      item.answer.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Highlight search terms in text
  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  const originalCategories = [
    { title: t('faq.categories.general'), items: [0, 1, 2] },
    { title: t('faq.categories.gettingStarted'), items: [3, 4, 5, 6] },
    { title: t('faq.categories.roles'), items: [7, 8, 9] },
    { title: t('faq.categories.courses'), items: [10, 11, 12, 13, 14, 15] },
    { title: t('faq.categories.analytics'), items: [16, 17, 18] },
    { title: t('faq.categories.technical'), items: [19, 20, 21, 22] },
    { title: t('faq.categories.security'), items: [23, 24, 25, 26] },
    { title: t('faq.categories.troubleshooting'), items: [27, 28, 29, 30, 31, 32] }
  ];

  // Filter categories based on search results
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return originalCategories;
    
    return originalCategories.map(category => ({
      ...category,
      items: category.items.filter(itemIndex => 
        filteredFAQData.some((_, index) => 
          faqData.indexOf(filteredFAQData[index]) === itemIndex
        )
      )
    })).filter(category => category.items.length > 0);
  }, [searchQuery, filteredFAQData, originalCategories]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 group"
          >
            <ArrowLeftIcon className="h-5 w-5 group-hover:-translate-x-1 transition-transform duration-200" />
            <span className="font-medium">{t('common.back')}</span>
          </button>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('faq.title')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {t('faq.subtitle')}
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8"
        >
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('faq.search.placeholder')}
              className="block w-full pl-10 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          
          {/* Search Results Count */}
          {searchQuery && (
            <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
              {filteredFAQData.length === 0 ? (
                <span className="text-red-500 dark:text-red-400">
                  {t('faq.search.noResults')}
                </span>
              ) : (
                <span>
                  {t('faq.search.resultsCount', { count: filteredFAQData.length })}
                </span>
              )}
            </div>
          )}
        </motion.div>

        {/* Table of Contents */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            {t('faq.tableOfContents')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(searchQuery ? filteredCategories : originalCategories).map((category, index) => (
              <a
                key={index}
                href={`#category-${index}`}
                className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors duration-200"
              >
                {category.title}
              </a>
            ))}
          </div>
        </motion.div>

        {/* FAQ Categories */}
        {(searchQuery ? filteredCategories : originalCategories).map((category, categoryIndex) => (
          <motion.div
            key={categoryIndex}
            id={`category-${categoryIndex}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 + categoryIndex * 0.1 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 border-b-2 border-primary-500 pb-2">
              {category.title}
            </h2>
            
            <div className="space-y-4">
              {category.items.map((itemIndex) => {
                const item = faqData[itemIndex];
                const isOpen = openItems.has(itemIndex);
                
                return (
                  <motion.div
                    key={itemIndex}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
                    whileHover={{ scale: 1.01 }}
                    transition={{ duration: 0.2 }}
                  >
                    <button
                      onClick={() => toggleItem(itemIndex)}
                      className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                    >
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white pr-4">
                        {searchQuery ? highlightText(item.question, searchQuery) : item.question}
                      </h3>
                      {isOpen ? (
                        <ChevronUpIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    
                    <motion.div
                      initial={false}
                      animate={{ height: isOpen ? 'auto' : 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4">
                        <div className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                          {searchQuery ? highlightText(item.answer, searchQuery) : item.answer}
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        ))}

        {/* Additional Resources */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mt-12"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            {t('faq.additionalResources.title')}
          </h2>
          <div className="space-y-2 text-gray-600 dark:text-gray-300">
            <p>{t('faq.additionalResources.whereToGetHelp')}</p>
            <p>{t('faq.additionalResources.howToProvideFeedback')}</p>
            <p>{t('faq.additionalResources.adminTraining')}</p>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
          className="text-center mt-12 text-sm text-gray-500 dark:text-gray-400"
        >
          <p>{t('faq.lastUpdated')}</p>
          <p className="mt-2">{t('faq.contactSupport')}</p>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
